import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Soft in-app permission prompt state.
 *
 * The OS-level push permission prompt can only fire ONCE per install on
 * iOS — a dismissal is permanent. Firing it in the confetti rush right
 * after onboarding meant most users tapped "Don't Allow" without
 * understanding the ask, and once dismissed they have no push token, so
 * retention-notifier can never reach them.
 *
 * This hook gates the OS prompt behind a value-prop sheet. Declining the
 * SHEET only records a soft-decline timestamp — it never calls the OS
 * prompt, so we preserve the ability to re-ask at a later high-value
 * moment (next completed workout, return visit, etc).
 *
 * Uses two separate localStorage keys:
 *   - `push_permission_prompted` (already used by usePushNotifications) —
 *     set only after the OS prompt has actually been shown.
 *   - `push_soft_prompt_declined_at` — set when the user taps "Not now"
 *     on the value-prop sheet. Used to gate re-asks by cool-off period.
 */
const OS_PROMPTED_KEY = 'push_permission_prompted';
const SOFT_DECLINED_AT_KEY = 'push_soft_prompt_declined_at';
const SOFT_ASK_COUNT_KEY = 'push_soft_prompt_ask_count';

// Cool-off between soft asks so we don't spam the user.
const SOFT_ASK_COOLOFF_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
// Cap total soft asks so we never become annoying — 3 tries max, then
// respect their choice.
const MAX_SOFT_ASKS = 3;

interface Eligibility {
  /** True when it's worth mounting the sheet at this trigger point. */
  eligible: boolean;
  /** False until the async permission check has completed. */
  resolved: boolean;
  /** Reason we returned false (for telemetry). */
  reason:
    | 'ok'
    | 'not_native'
    | 'os_already_prompted'
    | 'permission_granted'
    | 'permission_denied'
    | 'cooling_off'
    | 'max_asks_reached';
}

export function usePushSoftPromptEligibility(): Eligibility & { refetch: () => void } {
  const [state, setState] = useState<Eligibility>({ eligible: false, resolved: false, reason: 'not_native' });

  const compute = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setState({ eligible: false, resolved: true, reason: 'not_native' });
      return;
    }
    // Once the OS prompt has fired, we can't ask again — respect the OS.
    if (localStorage.getItem(OS_PROMPTED_KEY) === 'true') {
      setState({ eligible: false, resolved: true, reason: 'os_already_prompted' });
      return;
    }
    try {
      const perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'granted') {
        setState({ eligible: false, resolved: true, reason: 'permission_granted' });
        return;
      }
      if (perm.receive === 'denied') {
        setState({ eligible: false, resolved: true, reason: 'permission_denied' });
        return;
      }
    } catch {
      // If we can't check, assume prompt is still available.
    }

    const askCount = parseInt(localStorage.getItem(SOFT_ASK_COUNT_KEY) ?? '0', 10) || 0;
    if (askCount >= MAX_SOFT_ASKS) {
      setState({ eligible: false, resolved: true, reason: 'max_asks_reached' });
      return;
    }
    const declinedAt = parseInt(localStorage.getItem(SOFT_DECLINED_AT_KEY) ?? '0', 10) || 0;
    if (declinedAt > 0 && Date.now() - declinedAt < SOFT_ASK_COOLOFF_MS) {
      setState({ eligible: false, resolved: true, reason: 'cooling_off' });
      return;
    }
    setState({ eligible: true, resolved: true, reason: 'ok' });
  }, []);

  useEffect(() => {
    void compute();
  }, [compute]);

  return { ...state, refetch: compute };
}

export function recordSoftPromptShown(): void {
  const prev = parseInt(localStorage.getItem(SOFT_ASK_COUNT_KEY) ?? '0', 10) || 0;
  localStorage.setItem(SOFT_ASK_COUNT_KEY, String(prev + 1));
}

export function recordSoftPromptDeclined(): void {
  localStorage.setItem(SOFT_DECLINED_AT_KEY, String(Date.now()));
}
